import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/lib/motion";

const PACKAGES = [
  {
    n: "01",
    title: "SEO & Local SEO",
    tag: "Websites & Conversion's engine",
    tagColor: "bg-[#FEF9C3] border-[#FACC15] text-[#713F12]",
    points: [
      "Technical & on-page optimization that clears what slows you down",
      "Local SEO: citations, local pages, and map-pack visibility",
      "Google Business Profile optimization",
      "Content strategy and competitive research built in",
    ],
    best: "Home-service companies and local businesses that need to be found first.",
  },
  {
    n: "02",
    title: "Google Ads Management",
    tag: "Paid search, done practically",
    tagColor: "bg-[#FFEDD5] border-[#FB923C] text-[#9A3412]",
    points: [
      "Search campaigns built around the jobs you actually want",
      "Remarketing that stays in front of past visitors",
      "Landing pages tuned for calls and form fills",
      "Budgets reviewed monthly — spend follows results",
    ],
    best: "Businesses that want leads now while SEO compounds.",
  },
  {
    n: "03",
    title: "Website Optimization",
    tag: "Turn traffic into leads",
    tagColor: "bg-[#CCFBF1] border-[#14B8A6] text-[#115E59]",
    points: [
      "Speed, mobile, and usability fixes that lift every channel",
      "Conversion-focused page structures and clear calls to action",
      "Tracking on calls, forms, and the numbers that matter",
      "Ongoing conversion tuning as we learn what your visitors do",
    ],
    best: "Sites that get traffic but not enough calls.",
  },
  {
    n: "04",
    title: "Website Development",
    tag: "Built to rank and convert",
    tagColor: "bg-[#FEE2E2] border-[#EF4444] text-[#991B1B]",
    points: [
      "New builds and redesigns — fast, modern, and easy to edit",
      "SEO-ready architecture from day one",
      "Content and photography direction included",
      "You own everything — domain, site, and data",
    ],
    best: "Businesses whose website is quietly costing them leads.",
  },
  {
    n: "05",
    title: "Marketing Consulting",
    tag: "A senior strategist in your corner",
    tagColor: "bg-flame/10 border-flame/40 text-flame",
    points: [
      "Practical marketing strategy — 1:1 with Todd Meisler",
      "Roadmaps, priorities, and plain-language direction",
      "Vendor and channel guidance — no upselling",
      "Growth strategies aligned across every channel",
    ],
    best: "Owners who want decades of marketing judgment on call.",
  },
  {
    n: "06",
    title: "GEO / SEO Program",
    tag: "Get found on Google and in AI",
    tagColor: "bg-[#FEF3C7] border-[#FFB300] text-[#8A5B00]",
    points: [
      "Full SEO for Google — site fixes, content, backlinks, Google Business Profile",
      "AI visibility: crawlers, published content and authority so ChatGPT, Gemini and Claude name you",
      "Live dashboard — your rankings and AI mentions, updated monthly",
      "6-month top-10 guarantee in AI search — or you stop paying",
    ],
    best: "Owners who want to be the answer on Google and in AI answers alike.",
    detail: "/ai-visibility",
  },
];

export default function PackagesPage() {
  return (
    <main data-testid="packages-page" className="grain relative">
      <Seo
        title="Packages — SEO, Google Ads & Websites | ZD Design"
        description="ZD Design's core offers — SEO & local SEO, Google Ads, website optimization, development, marketing consulting and the GEO / SEO Program. By Todd Meisler."
        path="/packages"
      />
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#F8F9FA] to-[#F1F3F5] pb-14 pt-36 sm:pt-44">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 lg:pr-[400px] xl:pr-[520px]">
          <img
            src="/img/packages-illustration.webp"
            alt="Packages illustration"
            data-testid="packages-hero-art"
            className="pointer-events-none absolute right-2 top-1/2 hidden w-[390px] -translate-y-1/2 select-none rounded-tr-[2.5rem] lg:block xl:right-8 xl:w-[480px]"
          />
          <FadeUp>
            <Eyebrow>Packages</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl">
              Six ways we grow your business
            </h1>
            <p className="mt-6 max-w-2xl text-xl leading-relaxed text-slate-800">
              Every engagement is scoped to your goals — no lock-ins, no
              jargon. Start with one package or combine them into the
              Cross-Web Repair Program.
            </p>
          </FadeUp>
        </div>
      </section>
      <section className="bg-white pb-8 pt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid gap-5 lg:grid-cols-2 lg:gap-6">
            {PACKAGES.map((p, i) => (
              <FadeUp
                key={p.n}
                delay={(i % 2) * 0.08}
                className="h-full"
              >
                <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:border-flame/40 hover:shadow-md sm:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <span
                      aria-hidden="true"
                      className="font-mono text-4xl font-bold text-slate-200 sm:text-5xl"
                    >
                      {p.n}
                    </span>
                    <span
                      className={`rounded-full border px-3 py-1 text-center font-mono text-[13px] uppercase tracking-[0.15em] ${p.tagColor}`}
                    >
                      {p.tag}
                    </span>
                  </div>
                  <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">
                    {p.title}
                  </h2>
                  <ul className="mt-4 space-y-2.5">
                    {p.points.map((pt) => (
                      <li
                        key={pt}
                        className="flex gap-2.5 text-base leading-relaxed text-slate-800"
                      >
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-auto pt-5 text-base leading-relaxed text-faint">
                    <span className="font-semibold text-slate-900">
                      Best for:
                    </span>{" "}
                    {p.best}
                  </p>
                  {p.detail && (
                    <Link
                      to={p.detail}
                      data-testid={`packages-detail-${p.n}`}
                      className="group mt-3 inline-flex items-center gap-1.5 font-mono text-[13px] font-semibold uppercase tracking-[0.18em] text-flame transition-colors hover:text-ember"
                    >
                      See the full program
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp>
            <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-2xl bg-slate-900 p-8 text-center sm:flex-row sm:text-left">
              <div>
                <h3 className="font-display text-2xl font-bold text-white">
                  Not sure which package fits?
                </h3>
                <p className="mt-2 text-base leading-relaxed text-slate-300">
                  Book a free 30-minute strategy call — we'll point you in the
                  right direction, even if it's not us.
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-center gap-3 sm:items-end">
                <Link
                  to="/free-strategy-call"
                  data-testid="packages-cta"
                  className="group inline-flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-bold uppercase tracking-[0.15em] text-white shadow-lg shadow-flame/25 transition-all hover:bg-[#E04B00]"
                >
                  Schedule a free strategy call
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
                <a
                  href="tel:8168986319"
                  data-testid="packages-phone"
                  className="inline-flex items-center gap-2 font-mono text-sm text-slate-300 transition-colors hover:text-flame"
                >
                  <Phone className="h-4 w-4" /> 816-898-6319
                </a>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
